import {usePharmacyResendEmailMutation} from '@app/redux/services/pharmacyService';
import {TError, TPharmacy} from '@app/types';
import showNotification from '@app/utils/showNotification';
import {TableProps} from 'antd';
import {useCallback, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {useSearchParams} from 'react-router-dom';
import {LogEvents, useSendEvent} from '@app/utils/logger';

interface IUsePharmacyListState {
  selectedPharmacyId: number | undefined;
  isDetailModalOpen: boolean;
  isInviteModalOpen: boolean;
}

interface IUsePharmacyListActions {
  onDetailModalOpen: (pharmacyId?: number) => void;
  onDetailModalClose: () => void;
  onInviteModalOpen: () => void;
  onInviteModalClose: () => void;
  onTableChange: TableProps<TPharmacy>['onChange'];
  resend: (id: number) => Promise<void>;
}

export const usePharmacyList = (): [
  IUsePharmacyListState,
  IUsePharmacyListActions,
] => {
  const {t} = useTranslation();
  const sendEvent = useSendEvent();
  const [state, setState] = useState<IUsePharmacyListState>({
    selectedPharmacyId: undefined,
    isDetailModalOpen: false,
    isInviteModalOpen: false,
  });

  const [, setSearchParams] = useSearchParams();
  const [resendEmail] = usePharmacyResendEmailMutation();

  const onTableChange: TableProps<TPharmacy>['onChange'] = (pagination) => {
    const {current, pageSize} = pagination;
    sendEvent(LogEvents.PHARMACY_TABLE_PAGINATE);
    setSearchParams({
      page: String(current),
      limit: String(pageSize),
    });
  };

  const onDetailModalOpen = useCallback(
    (pharmacyId?: number) => {
      setState((prevState) => ({
        ...prevState,
        selectedPharmacyId: pharmacyId,
        isDetailModalOpen: true,
        isInviteModalOpen: false,
      }));
    },
    [setState],
  );

  const onDetailModalClose = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      selectedPharmacyId: undefined,
      isDetailModalOpen: false,
    }));
  }, [setState]);

  const onInviteModalOpen = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      isDetailModalOpen: false,
      isInviteModalOpen: true,
    }));
  }, [setState]);

  const onInviteModalClose = useCallback(() => {
    setState((prevState) => ({
      ...prevState,
      isInviteModalOpen: false,
    }));
  }, [setState]);

  const resend = async (id: number) => {
    try {
      sendEvent(LogEvents.PHARMACY_RESEND_INVITATION_PRESS, {pharmacyId: id});
      await resendEmail({id: id}).unwrap();
      showNotification(
        t('Success'),
        t('Email has been sent.'),
        'bottomRight',
        'success',
      );
    } catch (error) {
      const {data: errorData} = error as TError;
      sendEvent(LogEvents.ERROR_PHARMACY_RESEND_INVITATION, errorData);
      showNotification(
        t('Error'),
        t('An error occurred while resending the email.'),
        'bottomRight',
        'error',
      );
    }
  };

  const actions: IUsePharmacyListActions = {
    onDetailModalOpen,
    onDetailModalClose,
    onInviteModalOpen,
    onInviteModalClose,
    onTableChange,
    resend,
  };

  return [state, actions];
};
